import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import { useFormState } from "../../../functions/useFormState";
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { selectControlLogicOnly, selectFieldDescriptions } from "../../../redux/selectors";
import { postData, deleteData } from "../../../redux/slices/apiCallSlice"; // Adjust the path as necessary
import { fetchJobDetails } from "../../../redux/slices/fetchJobDetailsSlice"; // Adjust the path as necessary
import { selectApiStatus } from "../../../redux/slices/apiStatusSlice"; // Import the selector
import NoRecordFoundAlert from "../../common/NoRecordFoundAlert";
import { postChangeLog } from "../../../redux/slices/postChangeLogSlice"; // Import postChangeLog

const ControlLogicOnly = ({ zone }) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const currentUser = userDetails?.name;
  const sectionName = "controlLogicOnly";

  const jobId = useSelector((state) => state.jobId.jobId);
  const apiUrls = useSelector(selectApiUrls);
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const storedJobDetails = useSelector(selectControlLogicOnly);
  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails || {};
  }, [storedJobDetails, activetab]);
  
  const apiStatus = useSelector(selectApiStatus); // Fetch API status from Redux
  const loading = apiStatus[sectionName] !== "success"; // Determine loading state based on API status

  const [error, setError] = useState(null);
  const { formData, setFormData } = useFormState([]);

  useEffect(() => {
    const filteredItems = storedJobDetails.filter(
      (item) => item.Zone === activetab && item.ProjectID === jobDetails.ProjectID
    );
  
    setFormData(filteredItems);
  }, [storedJobDetails, activetab, jobDetails, setFormData]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur, showNotification, saveStatus } =
    useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const isFieldTbd = (fieldValue) => {
    if (typeof fieldValue === "string") {
      fieldValue = fieldValue.trim().toLowerCase();
      return fieldValue.includes("tbd");
    }
    return false;
  };

  const evaluateZoneTbdFields = (item) => {
    if (!item) return {};
  
    return Object.keys(item).reduce((acc, key) => {
      acc[key] = isFieldTbd(item[key]);
      return acc;
    }, {});
  };

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  const handleAddItem = async () => {
    const newItem = {
      projectID: jobId,
      zone: activetab,
      saleQuoteID: jobDetails.SaleQuoteID || "",
      hasControlLogic: "true",
    };
  
    // Validate newItem object
    if (!newItem.projectID || !newItem.zone || !newItem.hasControlLogic) {
      setError("All required fields must be filled out before adding an item.");
      return;
    }
  
    try {
      // Post the new item
      await dispatch(postData({ url: apiUrl, data: newItem })).unwrap();
  
      // Post to change log after successful addition
      await dispatch(postChangeLog({
        projectID: jobId,
        currentUser: currentUser,
        userRole: Object.keys(roles).find(role => roles[role]) || "Unknown",
        section: "Control Logic",
        field: "Control Logic Item",
        prevValue: "",
        newValue: "ADDED",
        dateTime: new Date().toISOString()
      })).unwrap();
  
      // Refetch job details
      await dispatch(fetchJobDetails({ jobId, apiUrls: { [sectionName]: apiUrl } })).unwrap();
  
      // Update form data
      const updatedDetails = await dispatch(fetchJobDetails({ jobId, apiUrls: { [sectionName]: apiUrl } })).unwrap();
      setFormData((prevData) => {
        const updatedFormData = [...prevData];
        if (updatedDetails[sectionName]) {
          updatedDetails[sectionName].forEach((item) => {
            if (item.Zone === activetab && item.ProjectID === jobDetails.ProjectID) {
              updatedFormData.push(item);
            }
          });
        }
        return updatedFormData;
      });
    } catch (error) {
      console.error("Failed to add item:", error);
      setError(error.message || "An error occurred while adding the item.");
    }
  };
  
  const handleRemoveItem = async (index) => {
    const itemId = formData[index]?.ID;
    const itemToDelete = formData[index];
    
    if (!itemId) {
      setError("Invalid item ID.");
      return;
    }
  
    try {
      // Delete the specified item and refetch job details
      await dispatch(deleteData({ url: apiUrl, deleteId: { ID: itemId } })).unwrap();

      // Only log if the item has meaningful data
      if (itemToDelete && (itemToDelete.Title || itemToDelete.Description || itemToDelete.Quantity)) {
        // Post to change log after successful deletion
        await dispatch(postChangeLog({
          projectID: jobId,
          currentUser: currentUser,
          userRole: Object.keys(roles).find(role => roles[role]) || "Unknown",
          section: "Control Logic",
          field: "Control Logic Item",
          prevValue: "",
          newValue: "DELETED",
          dateTime: new Date().toISOString()
        })).unwrap();
      }
  
      const updatedDetails = await dispatch(
        fetchJobDetails({ jobId, apiUrls: { [sectionName]: apiUrl } })
      ).unwrap();
  
      setFormData((prevData) => {
        const updatedFormData = prevData.filter((_, i) => i !== index);
        if (updatedDetails[sectionName]) {
          updatedDetails[sectionName].forEach((item) => {
            if (item.Zone === activetab && item.ProjectID === jobDetails.ProjectID) {
              updatedFormData.push(item);
            }
          });
        }
        return updatedFormData;
      });
    } catch (error) {
      console.error("Failed to delete item:", error);
      setError(error.message || "An error occurred while deleting the item.");
    }
  };

  const handleChangeItem = (e, index) => {
    const { name, value } = e.target;
    setFormData((prevData) =>
      prevData.map((item, i) =>
        i === index ? { ...item, [name]: value } : item
      )
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  if (!loading && !jobDetails?.ID) {
    return (
      <NoRecordFoundAlert sectionName={sectionName} zone={zone} />
    ); // Show no record found alert if no job details
  }

  return (
    <div>
      {error ? (
        <p style={{ color: "red" }}>Error: {error}</p>
      ) : loading ? (
        <p>Loading...</p>
      ) : (
        <div>
          <div>
            {showNotification && (
              <div
                className={`save-notification ${
                  saveStatus.includes("Failed") ? "error" : ""
                }`}
              >
                {saveStatus}
              </div>
            )}
          </div>
          <div className="control-logic-responsive">
          {formData.map((data, index) => {
          const zoneTbdFields = evaluateZoneTbdFields(data);
          return (
            <Row key={data.ID} className="mb-3 control-logic-record" style={{ border: "1px solid #ccc", padding: "10px", borderRadius: "5px" }}>
              <Col className="control-logic-fields">
                <Row>
                  {/* Title */}
                  <Col className="control-logic-field control-logic-title-field">
                    <Form.Group as={Row} controlId={`Title-${data.ID}`}>
                      <Form.Label column md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("Title")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Title</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col>
                        <Form.Control
                          type="text"
                          name="Title"
                          value={data.Title || ""}
                          onFocus={handleFocus}
                          onBlur={(e) => handleBlur(e, "Title", [], {
                            ID: data.ID,
                            url: apiUrl,
                            field: "Title",
                            tableKey,
                          })}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={zoneTbdFields?.Title ? "tbd-highlight" : ""}
                          onChange={(e) => handleChangeItem(e, index)}
                          placeholder="Enter Title"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  {/* Quantity */}
                  <Col md={3} className="control-logic-field control-logic-quantity-field">
                    <Form.Group as={Row} controlId={`Quantity-${data.ID}`}>
                      <Form.Label column md={3}>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("Quantity")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Quantity</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col sm={9}>
                        <Form.Control
                          type="text"
                          name="Quantity"
                          value={data.Quantity || ""}
                          onFocus={handleFocus}
                          onBlur={(e) => handleBlur(e, "Quantity", [], {
                            ID: data.ID,
                            url: apiUrl,
                            field: "Quantity",
                            tableKey,
                          })}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={zoneTbdFields?.Quantity ? "tbd-highlight" : ""}
                          onChange={(e) => handleChangeItem(e, index)}
                          placeholder="Enter Quantity"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mt-3">
                  {/* Description */}
                  <Col className="control-logic-field control-logic-description-field">
                    <Form.Group as={Row} controlId={`Description-${data.ID}`}>
                      <Form.Label>
                        <OverlayTrigger
                          placement="top"
                          overlay={renderTooltip("Description")}
                          delay={{ show: 300, hide: 200 }}
                        >
                          <span>Description</span>
                        </OverlayTrigger>
                      </Form.Label>
                      <Col>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="Description"
                          value={data.Description || ""}
                          onFocus={handleFocus}
                          onBlur={(e) => handleBlur(e, "Description", [], {
                            ID: data.ID,
                            url: apiUrl,
                            field: "Description",
                            tableKey,
                          })}
                          disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                          className={zoneTbdFields?.Description ? "tbd-highlight" : ""}
                          onChange={(e) => handleChangeItem(e, index)}
                          placeholder="Enter Description"
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
              {/* Delete Button */}
              <Col sm={1} className="control-logic-actions">
                <Button
                  variant="link"
                  onClick={() => handleRemoveItem(index)}
                  disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                >
                  <i className="bi bi-trash"></i>
                </Button>
              </Col>
            </Row>
          );
        })}
          <Button
            className="control-logic-add-button"
            variant="primary"
            onClick={handleAddItem}
            disabled={isDisabled || (!roles.Admin && !roles.Apps)}
          >
            Add Item
          </Button>
          </div>

        </div>
      )}
    </div>
  );
};

export default ControlLogicOnly;
